import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, Container, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { GridCellExpand } from '../../components/Table/components/gridCellExpand';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { analyticsSelector } from '../../redux/modules/analytics/analytics.selectors';
import { fetchAdjustedDashboard } from '../../redux/modules/analytics/analytics.actions';

export function AdjustReports({ dateRange }: { dateRange: { start_date: string | null; end_date: string | null } }) {
  const { adjustReports, fetchAdjustedDashboardStatus, error } = useSelector(analyticsSelector);
  const isLoading = fetchAdjustedDashboardStatus === fetchStatuses.pending;
  const hasError = fetchAdjustedDashboardStatus === fetchStatuses.rejected;
  const tableRef = useRef<TableRef>(null);

  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      sortable: false,
    },
    {
      field: 'report_date',
      headerName: 'Report Date',
      width: 120,
      sortable: false,
    },
    {
      field: 'network_name',
      headerName: 'Network',
      width: 120,
      valueFormatter: (params) => params.value || 'N/A',
      sortable: false,
    },
    {
      field: 'campaign_name',
      headerName: 'Campaign',
      width: 150,
      valueFormatter: (params) => params.value || 'N/A',
      sortable: false,
    },
    {
      field: 'clicks',
      headerName: 'Clicks',
      width: 100,
      type: 'number',
      sortable: false,
    },
    {
      field: 'installs',
      headerName: 'Installs',
      width: 100,
      type: 'number',
      valueFormatter: (params) => params.value || 'N/A',
      sortable: false,
    },
    {
      field: 'cost',
      headerName: 'Cost',
      width: 100,
      type: 'number',
      valueFormatter: (params) => params.value || 'N/A',
      sortable: false,
    },
    {
      field: 'currency',
      headerName: 'Currency',
      width: 100,
      valueFormatter: (params) => params.value || 'N/A',
      sortable: false,
    },
    {
      field: 'impressions',
      headerName: 'Impressions',
      width: 120,
      type: 'number',
      valueGetter: (params) => params.row.jsonResponse?.impressions || 0,
      sortable: false,
    },
    {
      field: 'conversions',
      headerName: 'Conversions',
      width: 120,
      type: 'number',
      valueGetter: (params) => params.row.jsonResponse?.conversions || 0,
      sortable: false,
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 120,
      valueGetter: (params) => params.row.jsonResponse?.custom_data?.source || 'N/A',
      sortable: false,
    },
    {
      field: 'campaignId',
      headerName: 'Campaign ID',
      width: 150,
      valueGetter: (params) => params.row.jsonResponse?.custom_data?.campaign_id || 'N/A',
      sortable: false,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      type: 'dateTime',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleString();
      },
      sortable: false,
    },
    {
      field: 'updated_at',
      headerName: 'Updated',
      type: 'dateTime',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleString();
      },
      sortable: false,
    },
    {
      field: 'jsonResponse',
      headerName: 'JSON Response',
      sortable: false,
      width: 350,
      valueFormatter: (params) => JSON.stringify(params.value, null, 2),
      renderCell: (params: GridRenderCellParams) => (
        <GridCellExpand value={JSON.stringify(params.value, null, 2) || ''} width={params.colDef.computedWidth} />
      ),
    },
  ]);
  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">Adjust Reports</Typography>
      </Box>

      <Box>
        {hasError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load adjust reports. {error?.message || 'Please try again.'}
          </Alert>
        )}
        <Table
          ref={tableRef}
          columns={columns}
          fetchItems={fetchAdjustedDashboard}
          fetchItemsCustomParams={{ start_date: dateRange.start_date, end_date: dateRange.end_date }}
          rows={adjustReports || []}
          loading={isLoading}
          rowCount={adjustReports?.length || 0}
          disableColumnFilter
          disableColumnMenu
          toolbarExportProps={{
            csvOptions: { fileName: `adjust-reports-${Date.now()}` },
            excelOptions: { fileName: `adjust-reports-${Date.now()}` },
          }}
        />
      </Box>
    </Container>
  );
}
